import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Timer, Play, Pause, RotateCcw, Copy, Link } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hrs > 0) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const JoinTimer = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [displayTime, setDisplayTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSession = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/timer/sessions/${code}`);
      setSession(response.data);
      setError(null);
      
      if (response.data.is_running && response.data.started_at) {
        const started = new Date(response.data.started_at).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - started) / 1000);
        const remaining = Math.max(0, (response.data.remaining_seconds || response.data.duration_seconds) - elapsed);
        setDisplayTime(remaining);
      } else {
        setDisplayTime(response.data.remaining_seconds || response.data.duration_seconds);
      }
    } catch (err) {
      setError("Session not found");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Timer countdown effect
  useEffect(() => {
    let interval;
    if (session?.is_running && displayTime > 0) {
      interval = setInterval(() => {
        setDisplayTime((prev) => {
          if (prev <= 1) {
            toast.success("Timer finished!", { duration: 5000 });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [session?.is_running, displayTime]);

  // Sync with server periodically
  useEffect(() => {
    if (session) {
      const syncInterval = setInterval(fetchSession, 5000);
      return () => clearInterval(syncInterval);
    }
  }, [session, fetchSession]);

  const controlTimer = async (action) => {
    if (!session) return;
    
    try {
      const response = await axios.put(`${API}/timer/sessions/${session.code}`, { action });
      setSession(response.data);
      if (action === "reset") {
        setDisplayTime(response.data.duration_seconds);
      }
      toast.success(`Timer ${action}${action === 'reset' ? '' : 'ed'}`);
    } catch (err) {
      toast.error(`Failed to ${action} timer`);
      console.error(err);
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Share link copied!");
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied!");
  };

  if (loading) {
    return (
      <Layout title="Loading..." subtitle="Connecting to timer session">
        <div className="max-w-2xl mx-auto">
          <Card className="tactical-card">
            <CardContent className="p-12 text-center">
              <Timer className="h-12 w-12 text-yellow-400 animate-pulse mx-auto mb-4" />
              <p className="text-muted-foreground">Loading timer session...</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Session Not Found" subtitle="The timer session could not be found">
        <div className="max-w-2xl mx-auto">
          <Card className="tactical-card border-red-500/30">
            <CardContent className="p-12 text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <Button 
                onClick={() => navigate("/execution/timer")}
                className="tactical-btn bg-yellow-600 hover:bg-yellow-700"
              >
                Create New Timer
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title={session?.name || "Timer"} 
      subtitle={`Session Code: ${code}`}
    >
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Timer Display */}
        <Card className={`tactical-card ${displayTime <= 60 && displayTime > 0 ? 'border-red-500/50' : 'border-yellow-500/30'}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg uppercase text-yellow-400">
                {session?.name}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={copyCode}
                  className="h-8"
                  data-testid="copy-code-button"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  {code}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={copyShareLink}
                  className="h-8"
                  data-testid="share-link-button"
                >
                  <Link className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Timer Display */}
            <div className="text-center py-8">
              <p className={`timer-huge ${displayTime <= 60 && displayTime > 0 ? 'text-red-500 animate-recording' : 'text-yellow-400'}`} data-testid="timer-display">
                {formatTime(displayTime)}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {session?.is_running ? "Running" : displayTime === 0 ? "Finished" : "Paused"}
              </p>
            </div>

            {/* Controls */}
            <div className="flex justify-center gap-4">
              {!session?.is_running ? (
                <Button 
                  onClick={() => controlTimer("start")}
                  className="tactical-btn bg-green-600 hover:bg-green-700 w-32"
                  disabled={displayTime === 0}
                  data-testid="start-button"
                >
                  <Play className="h-5 w-5 mr-2" />
                  Start
                </Button>
              ) : (
                <Button 
                  onClick={() => controlTimer("pause")}
                  className="tactical-btn bg-yellow-600 hover:bg-yellow-700 w-32"
                  data-testid="pause-button"
                >
                  <Pause className="h-5 w-5 mr-2" />
                  Pause
                </Button>
              )}
              <Button 
                onClick={() => controlTimer("reset")}
                variant="outline"
                className="tactical-btn w-32"
                data-testid="reset-button"
              >
                <RotateCcw className="h-5 w-5 mr-2" />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Share Info */}
        <Card className="tactical-card">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground text-center">
              This timer syncs across all devices viewing this session
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default JoinTimer;
